<template>
  <div>
    <h4 class="mb-3">{{ $t("process-data.bulk-datasubject") }}</h4>
    <!-- TKP-200 m.18: onay açıkken de kullanılır — ekleme düğüm başına taslağa yazılır -->
    <div v-if="bulkLocked" class="small text-info mb-2" data-test="bulk-drafts"><i class="bx bx-edit"></i> {{ $t("process-data.bulk-datasubject-drafts") }}</div>
    <div>
    <p class="text-muted">{{ $t("process-data.bulk-datasubject-hint", { process: $options.filters.translate(process && process.name) }) }}</p>
    <filter-select id="bulkDatasubjectFilter" ref="bulkDatasubjectFilter" :placeholder="$t('datasubject.select')" :multiple="true" @filter="onSearch" :selected="datasubjects" @changed="onSelect" />
    <sweet-button @click="assign()" type="submit" :loading="isUpdating" :disabled="isUpdating || datasubjects.length === 0" variant="success" icon="mdi mdi-check" size="sm" buttonClass="mt-3">
      {{ $t("common.save") }}
    </sweet-button>
    <div v-if="result" class="alert alert-info mt-3">{{ $t("process-data.bulk-result", result) }}</div>
    </div>
  </div>
</template>

<script>
import BulkLock from "@/modules/approval/bulk-lock"; // TKP-130
import rest from "@/rest-client";
import { bulkDatasubjectDraftPlan } from "./bulk-datasubject-drafts";
import { clipTargets, runInBackground } from "@/modules/approval/draft-batch"; // TKP-237 t.8, TKP-242
import { cachedPicker, isLibraryPick, mergeOrgAndLibrary, searchRequest } from "../unified-picker"; // TKP-241 p.7

const PICK_LIMIT = 1000;
/** TKP-241 p.7: toplu atama yükü — organizasyon ilgili kişileri `datasubjects`, kütüphane kalemleri `libraryItems`. */
export const bulkAssignPayload = (process, items = []) => {
  const library = (items || []).filter(isLibraryPick).map(({ fromLibrary, ...item }) => item);
  const own = (items || []).filter((i) => !isLibraryPick(i));
  return { process, datasubjects: own, ...(library.length ? { libraryItems: library } : {}) };
};
/** TKP-27 / 6.1 (kriter 3) — ilgili kişileri sürecin TÜM veri düğümlerine bağlar; zaten olan atlanır, veri bazında silme kartta. */
export default {
  mixins: [BulkLock],
  props: ["process"],
  data() {
    return { datasubjects: [], result: undefined, picker: null };
  },
  computed: {
    isUpdating() {
      return this.$store.getters["organizationProcessDataDatasubject/isUpdating"];
    },
  },
  methods: {
    /**
     * TKP-241 p.7: liste organizasyon ilgili kişileri + çalışma alanı kütüphanesinde organizasyonda karşılığı olmayanlar (`fromLibrary`),
     * Türkçe alfabeyle; panel başına bir kez yüklenir. Eskiden yalnız organizasyon sözlüğü (ilk 100) geliyordu.
     */
    async onSearch(value, callback) {
      if (!this.picker) {
        this.picker = cachedPicker(async (v) => {
          const request = searchRequest(v, this.currentLanguage, PICK_LIMIT);
          const [org, library] = await Promise.all([
            this.$store.dispatch("organizationDatasubject/paginateForSearch", request),
            this.$store.dispatch("datasubject/paginateForSearch", request).catch(() => ({ data: { docs: [] } })),
          ]);
          const lang = this.currentLanguage || "tr";
          const label = (d) => String((d && d.name && (d.name[lang] || d.name.tr || d.name.en)) || "").trim();
          return mergeOrgAndLibrary(org.data?.docs ?? [], library.data?.docs ?? []).sort((a, b) => label(a).localeCompare(label(b), lang));
        }, PICK_LIMIT);
      }
      callback(await this.picker(value));
    },
    onSelect(items) {
      this.datasubjects = items ?? [];
    },
    async assign() {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) return;
      try {
        if (this.bulkLocked) return await this.assignAsDrafts();
        // TKP-241 p.7: kütüphane kalemi `libraryItems` ile gider (uç organizasyonda karşılığını bulur ya da açar)
        const { data } = await this.$store.dispatch("organizationProcessDataDatasubject/bulkAssign", bulkAssignPayload(this.process, this.datasubjects));
        this.result = data;
        this.datasubjects = [];
        this.$toast.success(this.$t("common.success"));
        this.$emit("onAssigned", data);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    /** TKP-200 m.18: onay açıkken — sürecin düğümleri ve satırları okunur, eksik her (düğüm, ilgili kişi) tekil uçla taslağa yazılır. */
    async assignAsDrafts() {
      const processId = String((this.process && this.process._id) || this.process || "");
      const nodesResponse = await rest.post("/process-data/paginate", { pagination: { options: { limit: 1000, page: 1 }, query: { process: { value: { _id: processId }, isObject: true } } } });
      const nodes = (nodesResponse.data && nodesResponse.data.docs) || [];
      // TKP-242: satırlar tek istekte (eskiden düğüm başına bir istek)
      const rowsByNode = {};
      const ids = nodes.map((n) => String(n._id));
      const rows = ids.length ? await rest.post("/process-data/datasubject/paginate", { pagination: { options: { limit: 10000, page: 1 }, query: { processData: { value: { $in: ids }, isObject: true } } } }) : { data: { docs: [] } };
      for (const id of ids) rowsByNode[id] = [];
      for (const row of (rows.data && rows.data.docs) || []) {
        const key = String((row.processData && row.processData._id) || row.processData || "");
        if (rowsByNode[key]) rowsByNode[key].push(row);
      }
      const steps = bulkDatasubjectDraftPlan(nodes, rowsByNode, this.datasubjects);
      let failed = 0;
      // TKP-237 t.8: "Sürecin Tüm Verilerine İlgili Kişi" — bütün adımlar onay ekranında tek satır
      const tr = (v) => this.$options.filters.translate(v) || undefined;
      const process = this.process && typeof this.process === "object" ? this.process : {};
      const meta = { kind: "ASSIGN_DATASUBJECTS", level: "PROCESS", path: { department: tr(process.department && process.department.name), process: tr(process.name) }, targets: clipTargets(this.datasubjects.map((d) => tr(d.name))) };
      // TKP-242 q.1: adımlar arka planda (tek istek), bitince sunucu tek bildirim gönderir
      const out = await runInBackground(meta, async () => {
        for (const step of steps) {
          try {
            if (isLibraryPick(step.datasubject)) await this.$store.dispatch("organizationProcessDataDatasubject/addFromLibrary", { processData: step.processData, libraryItem: { ...step.datasubject, fromLibrary: undefined } });
            else await this.$store.dispatch("organizationProcessDataDatasubject/create", step);
          } catch (error) {
            failed++;
          }
        }
      }, (body) => this.$store.dispatch("approval/startBatch", body));
      this.result = undefined; // sonuç arka plan bildiriminde (yazılan/taslağa düşen sayısı)
      const queued = { nodes: nodes.length, datasubjects: this.datasubjects.length, queued: out.total || 0, skipped: nodes.length * this.datasubjects.length - steps.length };
      this.datasubjects = [];
      if (out.total) this.$toast.info(this.$t("approval.batch.queued", { steps: out.total }));
      else this.$toast.info(this.$t("inventory.hierarchy.nothing-to-write"));
      this.$emit("onAssigned", queued);
    },
  },
};
</script>
